<template>
  <a-modal
    v-model:visible="visibility"
    title="Editar vigencia de asociación carga-cobertura"
    :closable="!loading"
    :maskClosable="!loading"
    @cancel="closeModal"
  >
    <a-spin :spinning="loading">
      <a-typography-title :level="5">
        Completa los siguientes datos.
      </a-typography-title>
      <a-form
        name="form"
        layout="vertical"
        ref="formRef"
        :model="formState"
        :rules="rules"
      >
        <a-form-item
          required
          name="requestStartDate"
          label="Fecha de solicitud de alta"
        >
          <a-date-picker
            format="DD-MM-YYYY"
            placeholder="Seleccione fecha"
            v-model:value="formState.requestStartDate"
            :style="{ width: '100%' }"
          />
        </a-form-item>
        <a-form-item
          required
          name="startDate"
          label="Fecha de inicio de vigencia"
        >
          <a-date-picker
            format="DD-MM-YYYY"
            placeholder="Seleccione fecha"
            v-model:value="formState.startDate"
            :style="{ width: '100%' }"
          />
        </a-form-item>
      </a-form>
    </a-spin>

    <template #footer>
      <a-button @click="closeModal" :disabled="loading">Cancelar</a-button>
      <a-button
        type="primary"
        @click="validateForm"
        :loading="loading"
        :disabled="saveChangesButtonDisabled"
      >
        Guardar cambios
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, reactive, computed, defineEmits, defineExpose } from 'vue';
import moment from 'moment';
import { normalizePicker, tryNormalizePicker } from '@/utils/calendarDay';
import { useStore } from 'vuex';
import { PATCH_EMPLOYEE_DEPENDENT } from '@/store/types';
import notification from '@/utils/notifications';

const emit = defineEmits(['patchedEmployeeDependent']);
const store = useStore();
const loading = ref(false);
const recordData = ref(null);
const visibility = ref(false);
const formRef = ref(null);

const rules = {
  startDate: [
    {
      required: true,
      type: 'object',
      trigger: 'change',
      message: 'Seleccione fecha'
    }
  ],
  requestStartDate: [
    {
      required: true,
      type: 'object',
      trigger: 'change',
      message: 'Seleccione fecha'
    }
  ]
};

const formState = reactive({
  startDate: null,
  requestStartDate: null
});

const saveChangesButtonDisabled = computed(
  () =>
    JSON.stringify({
      startDate: formState.startDate
        ? tryNormalizePicker(formState.startDate)
        : null,
      requestStartDate: formState.requestStartDate
        ? tryNormalizePicker(formState.requestStartDate)
        : null
    }) ===
    JSON.stringify({
      startDate: recordData.value?.startDate
        ? tryNormalizePicker(recordData.value?.startDate)
        : null,
      requestStartDate: recordData.value?.requestStartDate
        ? tryNormalizePicker(recordData.value?.requestStartDate)
        : null
    })
);

// Null-safe preload: a bad record date leaves the field empty instead of
// throwing inside openModal and breaking the render. parseZone keeps the
// stored instant's offset so the displayed day (and every later
// normalizePicker read) is host-independent.
const preloadDate = (value) => {
  if (!value) return null;
  const iso = tryNormalizePicker(value);
  return iso ? moment.parseZone(iso) : null;
};

const openModal = (record) => {
  recordData.value = record;
  visibility.value = true;
  formState.requestStartDate = preloadDate(record.requestStartDate);
  formState.startDate = preloadDate(record.startDate);
};

const closeModal = () => {
  visibility.value = false;
  formRef.value?.resetFields();
};

const validateForm = () => {
  formRef.value?.validate().then((form) => {
    patchEmployeeDependent(form);
  });
};

const patchEmployeeDependent = async (data) => {
  loading.value = true;
  try {
    await store.dispatch(PATCH_EMPLOYEE_DEPENDENT, {
      employeeCoverageId: recordData.value.employeeCoverageId,
      employeeCoverageDependentId: recordData.value.employeeCoverageDependentId,
      payload: Object.fromEntries(
        Object.entries(data).map(([field, value]) => [
          field,
          normalizePicker(value)
        ])
      )
    });
    notification(
      'success',
      'Correcto',
      'Vigencia de la asociación carga-cobertura actualizada'
    );
    closeModal();
    emit('patchedEmployeeDependent');
  } catch (error) {
    notification('error', 'Error', error?.response?.data?.message);
  }
  loading.value = false;
};

defineExpose({ openModal });
</script>
